"use client";

// ============================================================
// CLUBHUB – Dokumente: die Ablage
// ============================================================
// Gebaut wie das DAM im Agent und wie jede Liste im Haus:
// Seitenkopf mit der Belegung, EINE Karte, darin der Filterkopf,
// links der Ordnerbaum, rechts der Inhalt als Raster oder Liste.
// Darueber liegen bei Bedarf das Seitenblatt einer Datei, die
// Sammelleiste der Markierten und die Fortschrittsanzeige.
//
// Diese Komponente haelt, was alle Teile teilen (Bestand, Adresse,
// offener Dialog, Hochladen) und rechnet aus der Adresse, was
// gezeigt wird: Startseite, Ordner, Suchtreffer oder alle Dateien
// einer Art. Die Teile holen es sich aus `ablage-kontext`.
//
// Belegung nur zur Auskunft — eine Grenze gibt es nicht.
//
// Mit Sitzung (`daten.quelle === "datenbank"`) geht jede Aenderung
// zuerst als Server Action an den Server (`app/(app)/dokumente/
// actions.ts`), danach holt `router.refresh()` den frischen Stand.
// Herunterladen und ZIP laufen ueber `/api/dokumente/…`, die Rechte
// prueft der Server. Ohne Recht „Dokumente bearbeiten“ fehlen alle
// Knoepfe, die etwas aendern.
// ============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Camera, Download, FilePlus2, FolderOpen, FolderPlus, FolderX, FolderInput, Search, Trash2, Upload } from "lucide-react";
import { Button, confirmDialog, EmptyState, PageHeader, toast } from "@/components/ui";
import { UeberAllem } from "@/components/ui/ueber-allem";
import { Listenblatt, Listenkoerper, Listenseite, Sammelleiste, useMarkierung } from "@/components/liste";
import { formatDecimal } from "@/lib/number-format";
import {
  DATEIARTEN,
  dateiArt,
  formatBytes,
  kinderVon,
  linkStatus,
  nachkommen,
  normalisiert,
  sortiereDateien,
  speicherort,
  zeitwert,
  type DateiArt,
} from "@/lib/dokumente/format";
import type { AblageDaten, DocumentView, FolderView } from "@/lib/dokumente/views";
import { ohneDateien, ohneOrdner, useBestand, zurueck } from "./ablage-zustand";
import { dateienLoeschen as dateienLoeschenAktion, ordnerLoeschen as ordnerLoeschenAktion } from "@/app/(app)/dokumente/actions";
import { errorOf } from "@/lib/action-result";
import { AblageKontext, type AblageKontextWert, type DialogZiel, type OrdnerZahl } from "./ablage-kontext";
import { useAdresse, useSpiegel, type Ansicht, type Darstellung } from "./adresse";
import { AblageFilterkopf } from "./ablage-filterkopf";
import { DateiBlatt } from "./datei-blatt";
import {
  DateienVerschiebenDialog,
  DateiUmbenennenDialog,
  HochladenZielDialog,
  OrdnerNeuDialog,
  OrdnerUmbenennenDialog,
  OrdnerVerschiebenDialog,
  RechteDialog,
  TeilenDialog,
} from "./dialoge";
import { Hochladeliste, useHochladen } from "./hochladen";
import { DateiListe, DateiRaster, OrdnerKopf, Startseite, ZIEH_TYP } from "./inhalt";
import { Ordnerbaum } from "./ordnerbaum";
import { VorlageBearbeitenDialog, VorlagenFilterkopf, VorlagenInhalt, VorlageVerwendenDialog } from "./vorlagen";

/** Laedt eine Adresse als Datei herunter, ohne die Seite zu verlassen. */
function laden(adresse: string) {
  const a = document.createElement("a");
  a.href = adresse;
  a.rel = "noopener";
  a.download = "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** So viele Dateien passen in eine ZIP-Adresse (Laenge der Adresse). */
const ZIP_HOECHSTENS = 150;

function zahlenRechnen(folders: FolderView[], documents: DocumentView[]): Map<string, OrdnerZahl> {
  const eltern = new Map(folders.map((f) => [f.id, f.parentId]));
  const zahlen = new Map<string, OrdnerZahl>();
  for (const d of documents) {
    let id: string | null | undefined = d.folderId;
    let schritte = 0;
    while (id && schritte < 32) {
      const z = zahlen.get(id) ?? { dateien: 0, groesse: 0, geaendert: "" };
      z.dateien += 1;
      z.groesse += d.size;
      if (!z.geaendert || zeitwert(d.updatedAt) > zeitwert(z.geaendert)) z.geaendert = d.updatedAt;
      zahlen.set(id, z);
      id = eltern.get(id);
      schritte += 1;
    }
  }
  return zahlen;
}

/** Eine Zeile ueber dem Inhalt, wenn kein Ordner offen ist (Suche, Art). */
function Hinweiszeile({ children, aktion }: { children: React.ReactNode; aktion?: React.ReactNode }) {
  return (
    <div className="border-border-subtle bg-bg-primary flex min-h-[45px] items-center gap-3 border-b py-1.5 pr-2 pl-4">
      <p className="text-body-sm text-text-secondary min-w-0 flex-1 truncate tabular-nums">{children}</p>
      {aktion}
    </div>
  );
}

export function Ablage({ daten, bereich }: { daten: AblageDaten; bereich: "ablage" | "vorlagen" }) {
  const { bestand, aendern } = useBestand(daten);
  const router = useRouter();
  const echt = daten.quelle === "datenbank";
  const schreiben = daten.darfSchreiben;
  const adresse = useAdresse();
  // Die Suchfelder spiegeln in die Adresse, haengen aber nicht an ihr
  // (`useSpiegel`): gefiltert wird mit dem getippten Text, sofort.
  const [suchtext, sucheMerken] = useSpiegel(adresse.ansicht.q);
  const [vorlagensuche, vorlagensucheMerken] = useSpiegel(adresse.ansicht.vq);
  const ansicht: Ansicht = { ...adresse.ansicht, q: suchtext, vq: vorlagensuche };
  const setzen: typeof adresse.setzen = (aenderungen, verlauf) => {
    if ("q" in aenderungen) sucheMerken(aenderungen.q ?? "");
    if ("vq" in aenderungen) vorlagensucheMerken(aenderungen.vq ?? "");
    adresse.setzen(aenderungen, verlauf);
  };
  const setzenHier: typeof adresse.setzenHier = (aenderungen) => {
    if ("vq" in aenderungen) vorlagensucheMerken(aenderungen.vq ?? "");
    adresse.setzenHier(aenderungen);
  };
  const [dialog, setDialog] = useState<DialogZiel | null>(null);
  const [ueber, setUeber] = useState(false);
  const knopf = useRef<HTMLInputElement>(null);
  const kamera = useRef<HTMLInputElement>(null);
  const hoch = useHochladen({ daten, anfang: daten });

  const zahlen = useMemo(() => zahlenRechnen(bestand.folders, bestand.documents), [bestand.folders, bestand.documents]);

  // ── Was gezeigt wird ───────────────────────────────────────
  const istAblage = bereich === "ablage";
  const ordner = istAblage && ansicht.ordner ? (bestand.folders.find((f) => f.id === ansicht.ordner) ?? null) : null;
  const unbekannt = istAblage && ansicht.ordner !== "" && !ordner;
  const q = normalisiert(ansicht.q.trim());
  const suche = istAblage && q.length > 0;
  const imOrdner = ordner ? new Set([ordner.id, ...nachkommen(ordner.id, bestand.folders)]) : null;

  const trefferAlle = suche ? bestand.documents.filter((d) => normalisiert(`${d.name} ${d.description ?? ""}`).includes(q)) : [];
  const trefferHier = imOrdner ? trefferAlle.filter((d) => d.folderId && imOrdner.has(d.folderId)) : trefferAlle;
  const nurHier = suche && ansicht.nurHier && Boolean(ordner);
  /** Gefundene Ordner — ueberall oder nur im offenen; gezaehlt wie in der Hinweiszeile. */
  const ordnerTreffer = (hier: boolean) =>
    suche ? bestand.folders.filter((f) => normalisiert(f.name).includes(q) && (!hier || !imOrdner || imOrdner.has(f.id))).length : 0;

  let umfang: DocumentView[] = [];
  let unterordner: FolderView[] = [];
  if (suche) {
    umfang = nurHier ? trefferHier : trefferAlle;
    unterordner = bestand.folders.filter((f) => normalisiert(f.name).includes(q) && (!nurHier || imOrdner!.has(f.id)));
  } else if (ordner) {
    umfang = bestand.documents.filter((d) => d.folderId === ordner.id);
    unterordner = kinderVon(ordner.id, bestand.folders);
  } else if (istAblage && ansicht.art) {
    umfang = bestand.documents;
  }

  const artZaehler = new Map<DateiArt, number>();
  for (const d of umfang) {
    const a = dateiArt(d.mimeType, d.name);
    artZaehler.set(a, (artZaehler.get(a) ?? 0) + 1);
  }
  const dateien = sortiereDateien(ansicht.art ? umfang.filter((d) => dateiArt(d.mimeType, d.name) === ansicht.art) : umfang, ansicht.sort);
  const ordnerListe = ansicht.art ? [] : unterordner;
  const startseite = istAblage && !suche && !ordner && !ansicht.art && !unbekannt;

  const bilder = umfang.filter((d) => dateiArt(d.mimeType, d.name) === "bild").length;
  const darstellung: Darstellung =
    ansicht.darstellung || (ordner && !suche && umfang.length > 0 && bilder / umfang.length >= 0.5 ? "raster" : "liste");

  const markierung = useMarkierung(dateien.map((d) => d.id));
  const offene = ansicht.datei ? bestand.documents.find((d) => d.id === ansicht.datei) : undefined;
  const reihe = offene && dateien.some((d) => d.id === offene.id) ? dateien : offene ? bestand.documents.filter((d) => d.folderId === offene.folderId) : [];
  const stelle = offene ? reihe.findIndex((d) => d.id === offene.id) : -1;
  // Das Blatt bleibt nach dem Schliessen mit der letzten Datei stehen,
  // damit es hinausfahren kann, statt zu verschwinden.
  const [zuletztOffen, setZuletztOffen] = useState<DocumentView | null>(null);
  if (offene && offene !== zuletztOffen) setZuletztOffen(offene);
  const blattDatei = offene ?? zuletztOffen;

  // Eine Datei, die NEBEN ein Ziel faellt (Seitenkopf, Filterkopf,
  // Abdunklung), oeffnete der Browser sonst selbst — und der Bestand
  // dieser Maske waere weg. Was ein Ziel genommen hat, ist schon
  // `defaultPrevented` und bleibt unberuehrt.
  useEffect(() => {
    const mitDateien = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    function drueber(e: DragEvent) {
      if (!mitDateien(e) || e.defaultPrevented) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
    }
    function fallen(e: DragEvent) {
      if (!mitDateien(e) || e.defaultPrevented) return;
      e.preventDefault();
      toast.message(
        bereich === "ablage" ? "Zum Hochladen auf einen Ordner oder die Dateifläche ziehen." : "Vorlagen kommen über „Neue Vorlage“ dazu.",
      );
    }
    window.addEventListener("dragover", drueber);
    window.addEventListener("drop", fallen);
    return () => {
      window.removeEventListener("dragover", drueber);
      window.removeEventListener("drop", fallen);
    };
  }, [bereich]);

  // ── Handgriffe ─────────────────────────────────────────────
  function zumOrdner(folderId: string | null) {
    setzen({ ordner: folderId, q: null, art: null, datei: null, in: null }, true);
  }

  function hochladen(eingang: File[] | FileList, folderId: string | null) {
    const liste = Array.from(eingang);
    if (liste.length === 0) return;
    if (!folderId) {
      setDialog({ art: "hochladen-ziel", dateien: liste });
      return;
    }
    if (!schreiben) return;
    void hoch.hochladen(liste, folderId);
  }

  async function dateienLoeschen(ids: string[]) {
    const weg = bestand.documents.filter((d) => ids.includes(d.id));
    if (weg.length === 0) return;
    const fassungen = weg.reduce((s, d) => s + d.versions.length, 0);
    const links = weg.reduce((s, d) => s + d.shares.filter((l) => linkStatus(l, daten.today) === "aktiv").length, 0);
    const ok = await confirmDialog({
      title: weg.length === 1 ? `„${weg[0]!.name}“ löschen?` : `${formatDecimal(weg.length)} Dateien löschen?`,
      description: `Mit ${fassungen === 1 ? "ihrer einen Fassung" : `${formatDecimal(fassungen)} Fassungen`}${links > 0 ? ` und ${formatDecimal(links)} aktiven Teilen-${links === 1 ? "Link" : "Links"}` : ""}. ${echt ? "Das lässt sich nicht rückgängig machen." : "Gleich danach lässt es sich noch rückgängig machen."}`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    // Server: alles oder nichts — Aufbewahrungsfrist und Belege von Rechnungen sperren.
    const fehler = errorOf(await dateienLoeschenAktion(ids));
    if (fehler) {
      toast.error(weg.length === 1 ? "Die Datei bleibt" : "Die Dateien bleiben", { description: fehler });
      return;
    }
    aendern((b) => ohneDateien(b, ids));
    markierung.leeren();
    if (ansicht.datei && ids.includes(ansicht.datei)) adresse.dateiSchliessen();
    if (echt) router.refresh();
    toast.success(
      weg.length === 1 ? "Datei gelöscht" : `${formatDecimal(weg.length)} Dateien gelöscht`,
      echt ? undefined : { action: { label: "Rückgängig", onClick: () => aendern((b) => zurueck(b, [], weg)) } },
    );
  }

  async function ordnerLoeschen(folderId: string) {
    const f = bestand.folders.find((x) => x.id === folderId);
    if (!f || f.parentId === null) return;
    const unter = nachkommen(f.id, bestand.folders);
    const alle = new Set([f.id, ...unter]);
    const weg = bestand.documents.filter((d) => d.folderId && alle.has(d.folderId));
    const ok = await confirmDialog({
      title: `Ordner „${f.name}“ löschen?`,
      description:
        weg.length === 0 && unter.length === 0
          ? "Der Ordner ist leer."
          : `Mit ${unter.length > 0 ? `${formatDecimal(unter.length)} Unterordnern und ` : ""}${formatDecimal(weg.length)} ${weg.length === 1 ? "Datei" : "Dateien"} darin. ${echt ? "Das lässt sich nicht rückgängig machen." : "Gleich danach lässt es sich noch rückgängig machen."}`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    const ordnerWeg = bestand.folders.filter((x) => alle.has(x.id));
    const fehler = errorOf(await ordnerLoeschenAktion(f.id));
    if (fehler) {
      toast.error(`„${f.name}“ bleibt`, { description: fehler });
      return;
    }
    aendern((b) => ohneOrdner(b, [...alle]));
    if (ansicht.ordner && alle.has(ansicht.ordner)) zumOrdner(f.parentId);
    if (echt) router.refresh();
    toast.success(
      `„${f.name}“ gelöscht`,
      echt ? undefined : { action: { label: "Rückgängig", onClick: () => aendern((b) => zurueck(b, ordnerWeg, weg)) } },
    );
  }

  function herunterladen(ids: string[]) {
    const auswahl = bestand.documents.filter((d) => ids.includes(d.id));
    if (auswahl.length === 0) return;
    const groesse = auswahl.reduce((s, d) => s + d.size, 0);
    if (echt) {
      if (auswahl.length === 1 && auswahl[0]!.adresse) {
        laden(auswahl[0]!.adresse);
        return;
      }
      if (auswahl.length > ZIP_HOECHSTENS) {
        toast.error(`Höchstens ${formatDecimal(ZIP_HOECHSTENS)} Dateien auf einmal`, { description: "Für mehr den ganzen Ordner als ZIP laden." });
        return;
      }
      laden(`/api/dokumente/zip?ids=${auswahl.map((d) => d.id).join(",")}`);
      toast.success(`ZIP mit ${formatDecimal(auswahl.length)} Dateien wird gepackt`, { description: formatBytes(groesse) });
      return;
    }
    toast.success(auswahl.length === 1 ? `„${auswahl[0]!.name}“ wird geladen` : `ZIP mit ${formatDecimal(auswahl.length)} Dateien wird gepackt`, {
      description: `${formatBytes(groesse)} · Musterdaten — im Betrieb kommt hier die Datei aus dem Speicher.`,
    });
  }

  function ordnerAlsZip(folderId: string) {
    const alle = new Set([folderId, ...nachkommen(folderId, bestand.folders)]);
    const drin = bestand.documents.filter((d) => d.folderId && alle.has(d.folderId));
    const name = bestand.folders.find((f) => f.id === folderId)?.name ?? "Ordner";
    if (drin.length === 0) {
      toast.message(`„${name}“ ist leer — da gibt es nichts zu packen.`);
      return;
    }
    if (echt) {
      laden(`/api/dokumente/zip?ordner=${folderId}`);
      toast.success(`„${name}.zip“ wird gepackt`, { description: `${formatDecimal(drin.length)} Dateien, ${formatBytes(drin.reduce((s, d) => s + d.size, 0))}` });
      return;
    }
    toast.success(`„${name}.zip“ wird gepackt`, {
      description: `${formatDecimal(drin.length)} Dateien, ${formatBytes(drin.reduce((s, d) => s + d.size, 0))} · Musterdaten — im Betrieb lädt hier das Paket.`,
    });
  }

  const kontext: AblageKontextWert = {
    daten,
    bestand,
    aendern,
    ansicht,
    setzen,
    setzenHier,
    dateiSchliessen: adresse.dateiSchliessen,
    oeffnen: setDialog,
    zumOrdner,
    hochladen,
    neueVersion: hoch.neueVersion,
    dateienLoeschen,
    ordnerLoeschen,
    herunterladen,
    ordnerAlsZip,
    zahlen,
  };

  const belegt = bestand.documents.reduce((s, d) => s + d.versions.reduce((t, v) => t + v.size, 0), 0);
  const zielName = ordner?.name ?? null;
  const markiertGroesse = bestand.documents.filter((d) => markierung.istMarkiert(d.id)).reduce((s, d) => s + d.size, 0);

  // ── Inhalt ─────────────────────────────────────────────────
  let inhalt: React.ReactNode;
  if (unbekannt) {
    inhalt = (
      <Listenkoerper className="p-5">
        <EmptyState
          icon={FolderX}
          title="Diesen Ordner gibt es nicht (mehr)"
          description="Vielleicht wurde er gelöscht oder verschoben, oder der Verweis ist alt."
          action={
            <Button variant="secondary" onClick={() => zumOrdner(null)}>
              Zur Übersicht
            </Button>
          }
        />
      </Listenkoerper>
    );
  } else if (startseite) {
    inhalt = (
      <Listenkoerper>
        <Startseite />
      </Listenkoerper>
    );
  } else if (ordnerListe.length === 0 && dateien.length === 0) {
    inhalt = (
      <Listenkoerper className="p-5">
        {suche ? (
          <EmptyState
            icon={Search}
            title={`Nichts gefunden zu „${ansicht.q.trim()}“`}
            description={
              nurHier
                ? `Gesucht nur in „${zielName}“ und darunter.`
                : "Gesucht in allen Ordnern, die du sehen darfst — in Namen und Beschreibungen."
            }
            action={
              <div className="flex flex-wrap justify-center gap-2">
                {nurHier && (
                  <Button variant="secondary" onClick={() => setzen({ in: null })}>
                    Überall suchen
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setzen({ q: null, in: null })}>
                  Suche leeren
                </Button>
              </div>
            }
          />
        ) : ansicht.art && umfang.length > 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={`Keine ${DATEIARTEN[ansicht.art].mehrzahl} ${ordner ? "in diesem Ordner" : ""}`.trim()}
            description="Die übrigen Dateien sind nur ausgeblendet."
            action={
              <Button variant="secondary" onClick={() => setzen({ art: null })}>
                Alle Arten zeigen
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={FolderOpen}
            title="Dieser Ordner ist leer"
            description={
              schreiben
                ? "Dateien hierher ziehen oder hochladen — vom Telefon auch direkt mit der Kamera, etwa ein unterschriebener Aufnahmeantrag."
                : "Hier liegt noch nichts."
            }
            action={
              schreiben && <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => knopf.current?.click()}>
                  <Upload className="h-4 w-4" aria-hidden />
                  Hochladen
                </Button>
                <Button variant="secondary" onClick={() => setDialog({ art: "ordner-neu", parentId: ordner?.id ?? null })}>
                  <FolderPlus className="h-4 w-4" aria-hidden />
                  Unterordner
                </Button>
              </div>
            }
          />
        )}
      </Listenkoerper>
    );
  } else if (darstellung === "raster") {
    inhalt = (
      <Listenkoerper className="p-4 md:p-5">
        <DateiRaster ordnerListe={ordnerListe} dateien={dateien} markierung={markierung} mitPfad={suche || !ordner} />
      </Listenkoerper>
    );
  } else {
    inhalt = <DateiListe ordnerListe={ordnerListe} dateien={dateien} markierung={markierung} mitPfad={suche || !ordner} />;
  }

  const zu = () => setDialog(null);

  return (
    <AblageKontext value={kontext}>
      <Listenseite>
        <PageHeader
          title="Dokumente"
          description={
            <span className="tabular-nums">
              {formatBytes(belegt)} in {formatDecimal(bestand.documents.length)} Dateien · Speicherort: {speicherort(daten.storage)}
            </span>
          }
          actions={
            !schreiben ? undefined : istAblage ? (
              <>
                <Button variant="secondary" onClick={() => setDialog({ art: "ordner-neu", parentId: ordner?.id ?? null })} title="Neuer Ordner">
                  <FolderPlus className="h-4 w-4" aria-hidden />
                  <span className="max-sm:sr-only">Neuer Ordner</span>
                </Button>
                <input
                  ref={kamera}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const liste = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    hochladen(liste, ordner?.id ?? null);
                  }}
                />
                <Button variant="secondary" size="icon" className="md:hidden" aria-label="Foto aufnehmen" onClick={() => kamera.current?.click()}>
                  <Camera className="h-4 w-4" aria-hidden />
                </Button>
                <input
                  ref={knopf}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const liste = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    hochladen(liste, ordner?.id ?? null);
                  }}
                />
                <Button onClick={() => knopf.current?.click()} title={zielName ? `In „${zielName}“ hochladen` : "Hochladen — danach den Ordner wählen"}>
                  <Upload className="h-4 w-4" aria-hidden />
                  Hochladen
                </Button>
              </>
            ) : (
              <Button onClick={() => setDialog({ art: "vorlage-bearbeiten", vorlageId: null })}>
                <FilePlus2 className="h-4 w-4" aria-hidden />
                Neue Vorlage
              </Button>
            )
          }
        />

        <Listenblatt>
          {istAblage ? (
            <AblageFilterkopf
              artZaehler={artZaehler}
              gesamt={umfang.length}
              ordnerName={zielName}
              darstellung={darstellung}
              trefferUeberall={trefferAlle.length + ordnerTreffer(false)}
              trefferHier={trefferHier.length + ordnerTreffer(true)}
            />
          ) : (
            <VorlagenFilterkopf />
          )}

          <div className="flex min-h-0 flex-1">
            <aside className="border-border-subtle bg-bg-primary hidden w-[232px] shrink-0 flex-col border-r md:flex">
              <Ordnerbaum bereich={bereich} />
            </aside>

            <div
              className="bg-bg-secondary relative flex min-h-0 min-w-0 flex-1 flex-col"
              // Ueber einem eigenen Ziel (Unterordner, Laufwerkkarte) zeigt
              // DAS Ziel den Rahmen; die Flaeche nimmt ihren zurueck. Die
              // Pruefung laeuft im Fangschritt, weil die Ziele das Ereignis
              // nicht weitergeben.
              onDragOverCapture={(e) => {
                if (!istAblage || !schreiben) return;
                const typen = Array.from(e.dataTransfer.types);
                if (!typen.includes("Files") || typen.includes(ZIEH_TYP)) return;
                setUeber(!(e.target instanceof Element && e.target.closest("[data-ablageziel]")));
              }}
              onDragOver={(e) => {
                if (!istAblage || !schreiben) return;
                const typen = Array.from(e.dataTransfer.types);
                if (!typen.includes("Files") || typen.includes(ZIEH_TYP)) return;
                e.preventDefault();
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setUeber(false);
              }}
              onDropCapture={() => setUeber(false)}
              onDrop={(e) => {
                if (!istAblage || !schreiben || e.dataTransfer.files.length === 0) return;
                e.preventDefault();
                hochladen(e.dataTransfer.files, ordner?.id ?? null);
              }}
            >
              {istAblage ? (
                <>
                  {ordner && !suche && <OrdnerKopf ordner={ordner} />}
                  {suche && (
                    <Hinweiszeile
                      aktion={
                        <Button size="sm" variant="ghost" onClick={() => setzen({ q: null, in: null })}>
                          Suche beenden
                        </Button>
                      }
                    >
                      {formatDecimal(dateien.length + ordnerListe.length)} Treffer für „{ansicht.q.trim()}“
                      {nurHier ? ` in „${zielName}“` : " in allen Ordnern"}
                    </Hinweiszeile>
                  )}
                  {!suche && !ordner && ansicht.art && (
                    <Hinweiszeile
                      aktion={
                        <Button size="sm" variant="ghost" onClick={() => setzen({ art: null })}>
                          Filter aufheben
                        </Button>
                      }
                    >
                      Alle {DATEIARTEN[ansicht.art].mehrzahl} aus allen Ordnern
                    </Hinweiszeile>
                  )}
                  {inhalt}
                </>
              ) : (
                <VorlagenInhalt />
              )}

              {ueber && (
                <div className="ring-accent bg-accent/[0.04] text-body-sm text-accent pointer-events-none absolute inset-0 z-20 flex items-center justify-center gap-2 font-medium ring-2 ring-inset">
                  <Upload className="h-4 w-4" aria-hidden />
                  {zielName ? `Loslassen — landet in „${zielName}“` : "Loslassen — danach den Ordner wählen"}
                </div>
              )}
            </div>
          </div>
        </Listenblatt>
      </Listenseite>

      {istAblage && (
        <Sammelleiste
          anzahl={markierung.markiert.length}
          wort={["Datei", "Dateien"]}
          zusatz={formatBytes(markiertGroesse)}
          onLeeren={markierung.leeren}
        >
          {schreiben && (
            <Button size="sm" variant="ghost" onClick={() => setDialog({ art: "dateien-verschieben", dateiIds: markierung.markiert })}>
              <FolderInput className="h-4 w-4" aria-hidden />
              <span className="max-sm:sr-only">Verschieben</span>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => herunterladen(markierung.markiert)}>
            <Download className="h-4 w-4" aria-hidden />
            <span className="max-sm:sr-only">Als ZIP</span>
          </Button>
          {schreiben && (
            <Button size="sm" variant="ghost" className="text-danger hover:text-danger" onClick={() => void dateienLoeschen(markierung.markiert)}>
              <Trash2 className="h-4 w-4" aria-hidden />
              <span className="max-sm:sr-only">Löschen</span>
            </Button>
          )}
        </Sammelleiste>
      )}

      {istAblage && blattDatei && (
        <DateiBlatt
          datei={blattDatei}
          offen={Boolean(offene)}
          onSchliessen={adresse.dateiSchliessen}
          vorher={stelle > 0 ? reihe[stelle - 1]!.id : null}
          nachher={stelle >= 0 && stelle < reihe.length - 1 ? reihe[stelle + 1]!.id : null}
        />
      )}

      {/* Fortschritt und Dialoge haengen am Rumpf und liegen UEBER dem
          Seitenblatt: von dort aus wird geteilt, verschoben, eine neue
          Version geladen — und das Blatt (Radix) sperrt sonst Zeiger und
          Fokus ausserhalb seiner selbst (`UeberAllem`, wie die Rueckfrage). */}
      {hoch.posten.length > 0 && (
        <UeberAllem>
          <Hochladeliste posten={hoch.posten} onSchliessen={hoch.aufraeumen} />
        </UeberAllem>
      )}

      {dialog && (
        <UeberAllem fokus onEscape={zu}>
          {dialog.art === "ordner-neu" && <OrdnerNeuDialog parentId={dialog.parentId} onClose={zu} />}
          {dialog.art === "ordner-umbenennen" && <OrdnerUmbenennenDialog folderId={dialog.folderId} onClose={zu} />}
          {dialog.art === "ordner-rechte" && <RechteDialog folderId={dialog.folderId} onClose={zu} />}
          {dialog.art === "ordner-verschieben" && <OrdnerVerschiebenDialog folderId={dialog.folderId} onClose={zu} />}
          {dialog.art === "dateien-verschieben" && (
            <DateienVerschiebenDialog dateiIds={dialog.dateiIds} onClose={zu} onFertig={markierung.leeren} />
          )}
          {dialog.art === "datei-umbenennen" && <DateiUmbenennenDialog dateiId={dialog.dateiId} onClose={zu} />}
          {dialog.art === "teilen" && <TeilenDialog dateiId={dialog.dateiId} onClose={zu} />}
          {dialog.art === "hochladen-ziel" && <HochladenZielDialog dateien={dialog.dateien} onClose={zu} />}
          {dialog.art === "vorlage-verwenden" && <VorlageVerwendenDialog vorlageId={dialog.vorlageId} onClose={zu} />}
          {dialog.art === "vorlage-bearbeiten" && <VorlageBearbeitenDialog vorlageId={dialog.vorlageId} onClose={zu} />}
        </UeberAllem>
      )}
    </AblageKontext>
  );
}

